import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, theme as antdTheme } from 'antd';
import React from 'react';
import { RouterProvider } from 'react-router';

import { router } from '@client/hooks/useRouteConfig';
import { useLocale } from '@client/store/locale.store';
import { useQueryClientStore } from '@client/store/queryClient.store';
import {
  getSystemTheme,
  resolveTheme,
  useTheme,
} from '@client/store/theme.store';
import { logger } from '@client/utils/logger.util';
import { applyThemeAttribute } from '@client/utils/theme.util';
import { EThemeMode, isRtlLocale } from '@common/types';
import { isDev } from '@common/utils';

import { RedirectProvider } from './context/RedirectContext';

import type { QueryClient } from '@tanstack/react-query';

const env = import.meta.env;

/**
 * AppWithQueryClient
 * @param {QueryClient} queryClient - The query client to use
 * @returns {React.JSX.Element} - The rendered component
 */
export function AppWithQueryClient({
  queryClient,
}: {
  queryClient: QueryClient;
}): React.JSX.Element {
  const setQueryClient = useQueryClientStore((state) => state.setQueryClient);
  const { locale } = useLocale();
  const { mode, resolved, setResolved } = useTheme();

  React.useEffect(() => {
    setQueryClient(queryClient); // Set the global query client to the zustand store
  }, [queryClient, setQueryClient]);

  React.useEffect(() => {
    applyThemeAttribute(resolved);
  }, [resolved]);

  React.useEffect(() => {
    if (mode !== EThemeMode.SYSTEM) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setResolved(getSystemTheme());
    mql.addEventListener('change', handler);
    setResolved(resolveTheme(EThemeMode.SYSTEM));
    return () => mql.removeEventListener('change', handler);
  }, [mode, setResolved]);

  return (
    <React.StrictMode>
      <ConfigProvider
        direction={isRtlLocale(locale) ? 'rtl' : 'ltr'}
        theme={{
          algorithm:
            resolved === EThemeMode.DARK
              ? antdTheme.darkAlgorithm
              : antdTheme.defaultAlgorithm,
        }}
      >
        <RedirectProvider>
          <RouterProvider router={router} />
        </RedirectProvider>
        {isDev(env, logger) ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
      </ConfigProvider>
    </React.StrictMode>
  );
}
